// Arquivo: src/components/EngineeringDashboard.tsx  (colar o conteúdo INTEGRAL no Code Mode)
// Orquestrador do painel: header, status, abas, filtros globais, sync real com
// polling, auto-refresh 120 s, footer de snapshot e drawer do Copilot.

import { useCallback, useEffect, useRef, useState } from 'react'
import type { DashboardResponse, DataFilters, FiltersInfo, JobInfo, Role, SnapshotRef, TreeNode } from '@/domain/engineering'
import { EMPTY_FILTERS } from '@/domain/engineering'
import { dashboardApi } from '@/lib/dashboardApi'
import { dateBR } from '@/lib/format'
import { CustoNivelView, DetalheView, IndicadoresView, TendenciaView } from '@/views/DashViews'
import { CopilotDrawer } from '@/components/CopilotDrawer'

const AUTO_REFRESH_MS = 120_000
const POLL_MS = 3_500

type TabKey = 'indicadores' | 'detalhe' | 'custo' | 'tendencia'

const TABS: Array<{ key: TabKey; label: string; icon: string }> = [
  { key: 'indicadores', label: 'Indicadores por Obra', icon: '▦' },
  { key: 'detalhe', label: 'Indicadores — Detalhe', icon: '☰' },
  { key: 'custo', label: 'Custo por Nível (WBS)', icon: '⛁' },
  { key: 'tendencia', label: 'Tabela de Tendência', icon: '📈' },
]

interface EngineeringDashboardProps {
  role: Role
  email: string
  onSignOut: () => void
}

export default function EngineeringDashboard({ role, email, onSignOut }: EngineeringDashboardProps) {
  const [activeTab, setActiveTab] = useState<TabKey>('indicadores')
  const [snapshot, setSnapshot] = useState<SnapshotRef | null>(null)
  const [filtersInfo, setFiltersInfo] = useState<FiltersInfo | null>(null)
  const [draft, setDraft] = useState<DataFilters>(EMPTY_FILTERS)
  const [applied, setApplied] = useState<DataFilters>(EMPTY_FILTERS)
  const [dashboardData, setDashboardData] = useState<DashboardResponse | null>(null)
  const [treeNodes, setTreeNodes] = useState<TreeNode[]>([])
  const [treeAvisos, setTreeAvisos] = useState<string[]>([])
  const [message, setMessage] = useState('Carregando dados...')
  const [messageError, setMessageError] = useState(false)
  const [loading, setLoading] = useState(true)
  const [jobStatus, setJobStatus] = useState('')
  const [copilotOpen, setCopilotOpen] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const loadCore = useCallback(async (filters: DataFilters, firstRun = false) => {
    if (firstRun) setLoading(true)
    setMessageError(false)
    try {
      const [dashboard, tree] = await Promise.all([
        dashboardApi.dashboard(filters),
        dashboardApi.tree(filters),
      ])
      setDashboardData(dashboard)
      setTreeNodes(tree.nodes ?? [])
      setTreeAvisos(tree.avisos ?? [])
      setMessage('Dados oficiais carregados com sucesso.')
    }
    catch (reason) {
      setMessageError(true)
      setMessage(reason instanceof Error ? reason.message : 'Falha ao carregar dados do snapshot.')
    }
    finally {
      setLoading(false)
    }
  }, [])

  const loadSnapshot = useCallback(async () => {
    try {
      const result = await dashboardApi.snapshot()
      setSnapshot(result.snapshot)
      if (result.job && (result.job.status === 'PENDENTE' || result.job.status === 'PROCESSANDO')) {
        startPolling(result.job.jobId)
      }
    }
    catch {
      setSnapshot(null)
    }
  }, [])

  const loadFilters = useCallback(async () => {
    try {
      const info = await dashboardApi.filters()
      setFiltersInfo(info)
      const inicio = info.dataInicio && info.dataInicio > '1900-01-01' ? info.dataInicio : ''
      const fim = info.dataFim ?? ''
      const next = { ...EMPTY_FILTERS, inicio, fim }
      setDraft(next)
      setApplied(next)
      return next
    }
    catch {
      return null
    }
  }, [])

  useEffect(() => {
    void (async () => {
      await loadSnapshot()
      const filters = await loadFilters()
      await loadCore(filters ?? EMPTY_FILTERS, true)
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const timer = setInterval(() => { void loadCore(applied) }, AUTO_REFRESH_MS)
    return () => clearInterval(timer)
  }, [applied, loadCore])

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
  }

  const startPolling = (jobId: string) => {
    if (pollRef.current) return
    setSyncing(true)
    setJobStatus('Sincronização em andamento no Sienge...')
    pollRef.current = setInterval(() => {
      void (async () => {
        try {
          const result = await dashboardApi.job(jobId)
          const job: JobInfo | null = result.job
          if (!job) { stopPolling(); setSyncing(false); return }
          setJobStatus(`${job.status}${job.etapa ? ` — ${job.etapa}` : ''}${job.progresso !== undefined ? ` (${job.progresso}%)` : ''}`)
          if (job.status === 'APROVADO') {
            stopPolling()
            setSyncing(false)
            setJobStatus('')
            setMessage('Sincronização concluída: novo snapshot homologado e publicado!')
            await loadSnapshot()
            const filters = await loadFilters()
            await loadCore(filters ?? applied)
          }
          else if (job.status === 'REPROVADO' || job.status === 'ERRO') {
            stopPolling()
            setSyncing(false)
            setJobStatus('')
            setMessageError(true)
            setMessage(job.erros?.[0] || 'Sincronização não aprovada; mantido o snapshot anterior.')
          }
        }
        catch {
          stopPolling()
          setSyncing(false)
          setJobStatus('')
          setMessageError(true)
          setMessage('Falha ao consultar o status da sincronização.')
        }
      })()
    }, POLL_MS)
  }

  useEffect(() => () => stopPolling(), [])

  const dispararSync = async () => {
    if (role !== 'admin' || syncing) return
    if (!window.confirm('Deseja iniciar a atualização oficial dos relatórios no portal Sienge (conector Windows + auditoria)?')) return
    setMessage('Rotina disparada. O painel continuará operacional enquanto o novo snapshot é processado.')
    setMessageError(false)
    try {
      const result = await dashboardApi.requestSync()
      setJobStatus('PENDENTE — aguardando conector Windows.')
      startPolling(result.jobId)
    }
    catch (reason) {
      setMessageError(true)
      setMessage(reason instanceof Error ? reason.message : 'Não foi possível solicitar a sincronização.')
    }
  }

  const unidadesDisponiveis = draft.obraId
    ? (filtersInfo?.unidades ?? []).filter(unidade => unidade.obraId === draft.obraId)
    : (filtersInfo?.unidades ?? [])

  const aplicar = () => {
    setApplied({ ...draft })
    void loadCore(draft)
  }

  const limpar = () => {
    const reset: DataFilters = { obraId: '', unidadeId: '', inicio: filtersInfo?.dataInicio && filtersInfo.dataInicio > '1900-01-01' ? filtersInfo.dataInicio : '', fim: filtersInfo?.dataFim ?? '' }
    setDraft(reset)
    setApplied(reset)
    void loadCore(reset)
  }

  return (
    <>
      <header className="app-header">
        <div className="header-left">
          <span className="logo-pill"><span className="logo-emblem">RDR</span> RDR ENGENHARIA</span>
          <span className="tagline">Controle Físico & Financeiro</span>
        </div>
        <div className="header-right">
          {role === 'admin' ? (
            <button type="button" className="btn-sync" disabled={syncing} onClick={() => void dispararSync()} title="Dispara o conector Windows e o ciclo de auditoria">
              <span aria-hidden>⟳</span> {syncing ? 'Sincronizando...' : 'Buscar dados no Sienge'}
            </button>
          ) : null}
          <button type="button" className="btn-copilot-header" onClick={() => setCopilotOpen(true)}>RDR Copilot <span className="ia-badge">IA</span></button>
          <span className="user-badge">
            <span className="user-email" title={email}>{email || 'usuário Lumi'}</span>
            <button type="button" className="btn-logout" onClick={onSignOut}>Sair</button>
          </span>
        </div>
      </header>

      <main className="app-main">
        <div className="status-bar">
          <div className="status-left">
            <span className={`status-dot ${messageError ? 'err' : ''}`} />
            <span className={`status-msg ${messageError ? 'err' : ''}`}>{message}</span>
          </div>
          {jobStatus ? <span className="job-status">{jobStatus}</span> : null}
        </div>

        <div className="tabs" role="tablist">
          {TABS.map(tab => (
            <button key={tab.key} type="button" role="tab" aria-selected={activeTab === tab.key}
              className={`tab ${activeTab === tab.key ? 'active' : ''}`} onClick={() => setActiveTab(tab.key)}>
              <span aria-hidden>{tab.icon}</span> {tab.label}
            </button>
          ))}
        </div>

        <div className="filters-card">
          <div className="filter-group">
            <span className="filter-label">Obra</span>
            <select value={draft.obraId} onChange={event => setDraft(prev => ({ ...prev, obraId: event.target.value, unidadeId: '' }))}>
              <option value="">{filtersInfo ? `Todas as Obras (Consolidado ${filtersInfo.obras.length} Obras)` : 'Todas as Obras'}</option>
              {(filtersInfo?.obras ?? []).map(obra => <option key={obra.id} value={obra.id}>{obra.name}</option>)}
            </select>
          </div>
          <div className="filter-group">
            <span className="filter-label">Unidade Construtiva</span>
            <select value={draft.unidadeId} onChange={event => setDraft(prev => ({ ...prev, unidadeId: event.target.value }))} disabled={!unidadesDisponiveis.length}
              title={unidadesDisponiveis.length ? undefined : 'Unidades não disponíveis nesta carga (reextração necessária)'}>
              <option value="">{unidadesDisponiveis.length ? 'Todas as Unidades' : 'Unidades indisponíveis nesta carga'}</option>
              {unidadesDisponiveis.map(unidade => (
                <option key={`${unidade.obraId}:${unidade.unidadeId}`} value={unidade.unidadeId}>{unidade.unidadeNome || unidade.unidadeId}</option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <span className="filter-label">Período Data Contábil</span>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input type="date" value={draft.inicio} onChange={event => setDraft(prev => ({ ...prev, inicio: event.target.value }))} />
              <span className="filter-sep">→</span>
              <input type="date" value={draft.fim} onChange={event => setDraft(prev => ({ ...prev, fim: event.target.value }))} />
            </div>
          </div>
          <button type="button" className="btn-primary" onClick={aplicar} disabled={loading}>Filtrar</button>
          <button type="button" className="btn-ghost" onClick={limpar}>Limpar</button>
        </div>

        {activeTab === 'indicadores' ? <IndicadoresView data={dashboardData} /> : null}
        {activeTab === 'detalhe' ? <DetalheView data={dashboardData} filters={applied} /> : null}
        {activeTab === 'custo' ? <CustoNivelView nodes={treeNodes} avisos={treeAvisos} filters={applied} /> : null}
        {activeTab === 'tendencia' ? <TendenciaView data={dashboardData} nodes={treeNodes} avisos={treeAvisos} /> : null}
      </main>

      <footer className="footer-bar">
        <span className="footer-text">RDR Engenharia & Empreendimentos LTDA • Sistema Integrado de Gestão e Controle</span>
        <span className="run-badge">
          {snapshot
            ? `Snapshot Ativo: ${snapshot.runId} (corte ${dateBR(snapshot.dataCorte ?? snapshot.periodoFim)})`
            : 'Nenhum snapshot aprovado em produção'}
        </span>
      </footer>

      <button type="button" className="fab" onClick={() => setCopilotOpen(true)}>✦ RDR Copilot</button>
      <CopilotDrawer open={copilotOpen} onClose={() => setCopilotOpen(false)} />
    </>
  )
}
